import React, { useState, useRef } from 'react';
import { renderIcon } from '../../../../FlowchartView/utils/renderIcon';
import IconPicker from '../../../IconPicker';
import EditableCardName from './EditableCardName';

const CLASS_PREFIX = 'mission-card';

/**
 * CardHeader Component
 *
 * Renders the mission card header with:
 * - Clickable icon (opens IconPicker)
 * - Editable name (click to edit, blur/enter to save)
 */
const CardHeader = ({
  task,
  effectiveIcon,
  hasIcon,
  category,
  isEditingTitle = false,
  isLocked = false,
  isExcluded = false,
  isWait = false,
  lockReason = '',
  onFinishTitleEdit,
  onRename,
  onIconChange,
}) => {
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPickerPosition, setIconPickerPosition] = useState(null);
  const iconButtonRef = useRef(null);

  const handleIconClick = (e) => {
    e.stopPropagation();
    setIconPickerPosition({
      x: e.clientX,
      y: e.clientY + 4,
    });
    setShowIconPicker(true);
  };

  const handleIconChange = (newIcon) => {
    onIconChange?.(task.id, newIcon);
  };

  return (
    <>
      <div className={`${CLASS_PREFIX}__header ${!hasIcon ? `${CLASS_PREFIX}__header--no-icon` : ''} ${!isLocked && !isExcluded && !isWait ? `${CLASS_PREFIX}__header--no-indicator` : ''}`}>
        {hasIcon && (
          <button
            ref={iconButtonRef}
            className={`${CLASS_PREFIX}__icon-btn ${CLASS_PREFIX}__icon-btn--has-icon`}
            onClick={handleIconClick}
            onMouseDown={(e) => e.stopPropagation()}
            title="Change icon"
          >
            {renderIcon(effectiveIcon, {
              size: '18px',
              nodeColor: category?.color,
              fallback: <span className={`${CLASS_PREFIX}__icon-placeholder`}>+</span>,
              onCustomIconError: onIconChange ? () => onIconChange(task.id, null) : undefined,
            })}
          </button>
        )}

        <div className={`${CLASS_PREFIX}__title-line`}>
          <EditableCardName
            task={task}
            onRename={onRename}
            onFinishTitleEdit={onFinishTitleEdit}
            isEditingTitle={isEditingTitle}
          />

          {isExcluded ? (
            <div
              className={`${CLASS_PREFIX}__excluded-indicator`}
              title="This path was not chosen — a different decision branch was selected"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`${CLASS_PREFIX}__excluded-icon`}
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="5.5" y1="5.5" x2="18.5" y2="18.5" />
              </svg>
            </div>
          ) : isLocked ? (
            <div
              className={`${CLASS_PREFIX}__lock-indicator`}
              title={lockReason || 'This mission is locked until conditions are met'}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`${CLASS_PREFIX}__lock-icon`}
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          ) : isWait ? (
            <div
              className={`${CLASS_PREFIX}__wait-indicator`}
              title="Waiting"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`${CLASS_PREFIX}__wait-icon`}
                aria-hidden="true"
              >
                <path d="M6 2h12" />
                <path d="M6 22h12" />
                <path d="M7 2c0 5 5 7 5 10s-5 5-5 10" />
                <path d="M17 2c0 5-5 7-5 10s5 5 5 10" />
              </svg>
            </div>
          ) : null}
        </div>
      </div>

      {showIconPicker && (
        <IconPicker
          currentIcon={effectiveIcon}
          onIconChange={handleIconChange}
          onIconDeleted={() => handleIconChange(null)}
          onClose={() => setShowIconPicker(false)}
          position={iconPickerPosition}
          nodeColor={category?.color}
        />
      )}
    </>
  );
};

export default CardHeader;
